import { ApPortalNameMap } from '@api/models/ap_portal';
import { defineStore } from 'pinia';

import { ApPortalProvider } from '@stuut/api/models';
import type { ApPortal, ApPortalAccount, Partner } from '@stuut/api/models';

import { stuut } from '@/api';
import { usePartners } from '@/store';

interface State {
  accounts: ApPortalAccount[];
}

export const useApPortalStore = defineStore('apPortal', {
  actions: {
    async createAccount(
      provider?: string,
      username?: string,
      password?: string,
      headerToken?: string,
    ) {
      const account = await stuut.apPortalAccounts.createAccount({
        header_token: headerToken,
        password,
        provider,
        username,
      });

      this.loadAccounts();

      return account;
    },

    async createPortal(data = {}, customer?: Partner) {
      const portal = await stuut.apPortals.createPortal(data);

      if (customer) this.syncPartnerStore(customer);

      return portal;
    },

    async deleteAccount(account) {
      await stuut.apPortalAccounts.delete(account?.id);

      this.loadAccounts();
    },

    async loadAccounts() {
      const accounts = await stuut.apPortalAccounts.list();

      this.$patch({ accounts });

      return this.accounts;
    },

    async removeApPortal(apPortal: ApPortal, customer?: Partner) {
      const portal = await stuut.apPortals.delete(apPortal?.id);

      if (customer) this.syncPartnerStore(customer);

      return portal;
    },

    async syncPartnerStore(customer: Partner) {
      if (!customer?.id) return;

      const partnerStore = usePartners();
      const partner = await stuut.partners.retrieve(customer?.id, customer);

      return partnerStore.syncRecord(partner);
    },

    async updateAccount(
      account: ApPortalAccount,
      username?: string,
      password?: string,
      headerToken?: string,
    ) {
      const updatedAccount = await stuut.apPortalAccounts.updateAccount(account, {
        header_token: headerToken,
        password,
        username,
      });

      this.loadAccounts();

      return updatedAccount;
    },

    async updatePortal(apPortal: ApPortal, data = {}, customer?: Partner) {
      const portal = await stuut.apPortals.updatePortal(apPortal, data);

      if (customer) this.syncPartnerStore(customer);

      return portal;
    },
  },

  getters: {
    getByProvider(state) {
      return (provider: ApPortalProvider) =>
        state.accounts.filter((acct) => acct.provider === provider);
    },

    providerOptions() {
      return Object.values(ApPortalProvider)?.map((provider) => ({
        label: ApPortalNameMap[provider] || provider,
        value: provider,
      }));
    },
  },

  state: (): State => ({
    accounts: [],
  }),
});

export default useApPortalStore;
